/**
 * 动态菜单 Hook
 * 将后端返回的菜单数据转换为 Ant Design Menu 组件所需格式
 */

import { useMemo, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import React from 'react';
import type { MenuProps } from 'antd';
import { getIcon } from '@/utils/iconRegistry';
import { getStorageItem, setStorageItem, STORAGE_KEYS } from '@/utils/storage';
import unifiedApi from '@/utils/unifiedApi';
import { WorkMode } from './workMode';
import { ensureHomeMenuItem, getHomePath } from './homeMenu';

/** 后端菜单数据结构 */
interface BackendMenuItem {
  id: number;
  parent_id: number;
  title: string;
  path: string;
  icon?: string;
  menu_type: number;
  sort_order: number;
  locale?: string;
  work_mode: string;
  name: string;
  target?: number;
  status: number;
  hide?: number;
  children?: BackendMenuItem[];
}

/** 图标名称到组件的映射
 *  REQ-20260621-0007: 无 icon / 未知 icon 的菜单兜底默认图标，保证每个导航项都有 icon。 */
const DEFAULT_MENU_ICON = 'AppstoreOutlined';
const getIconComponent = (iconName?: string): React.ReactNode => {
  // 通过 iconRegistry 按需取图标（仅打包实际注册的图标，避免全量 barrel 导入拖慢首屏）。
  // 未注册 / 无 icon 的菜单兜底默认图标，保证每个导航项都有 icon。
  const IconComponent = getIcon(iconName) ?? getIcon(DEFAULT_MENU_ICON);
  return IconComponent ? React.createElement(IconComponent) : undefined;
};

/** 将后端菜单树转换为 Ant Design MenuProps['items'] */
const convertMenuItems = (
  menus: BackendMenuItem[],
  t: (key: string) => string,
): MenuProps['items'] => {
  if (!menus || menus.length === 0) return [];

  // REQ-20260621-0007: 过滤 hide===1 的菜单项（之前只在未使用的 generateRoutes 死代码里过滤，
  // 导致 fnd_menus.hide=1 的菜单仍显示在左侧导航）。
  return menus
    .filter((menu) => menu.hide !== 1)
    .map((menu) => {
    const label = menu.locale ? t(menu.locale) : menu.title;
    const icon = getIconComponent(menu.icon);

    if (menu.children && menu.children.length > 0) {
      return {
        key: menu.path,
        icon,
        label,
        children: convertMenuItems(menu.children, t),
      };
    }

    return {
      key: menu.path,
      icon,
      label,
    };
  });
};

/**
 * 菜单缓存 key。
 * - v2 (REQ-20260621-0007): bump 失效旧缓存，让 hide 过滤即时生效
 * - v3 (导航整合收口): 订单中心/客户中心/报表中心整合 + 经销商导航统一。
 *   旧浏览器里残留的 v2 缓存会持续渲染整合前的旧导航（含已下线菜单），
 *   bump 到 v3 强制所有用户首帧即丢弃旧缓存、重新从后端拉取新导航。
 * - v4 (小程序管理菜单去重): fnd_menus 历史重复的「小程序管理」树被清理后，
 *   旧缓存仍会渲染两个「小程序管理」，bump 到 v4 丢弃旧缓存。
 * - v5 (售后菜单授权修复): 旧「退货订单」授权迁移到「售后单」入口后，
 *   丢弃角色授权调整前的菜单缓存。
 */
const MENU_CACHE_KEY_PREFIX = 'menu_cache_v5_';
const EMPTY_MENUS: BackendMenuItem[] = [];

/**
 * 动态菜单 Hook
 * 根据工作模式从后端获取菜单并转换为 Ant Design 格式
 */
export const useMenuItems = (workMode: WorkMode) => {
  const { t } = useTranslation();
  // 菜单数据带上所属模式：切换模式的那一帧，旧模式的菜单不能当成新模式的用（标签页会据此开错标签）。
  // 初始值直接读缓存，刷新时第一帧就有菜单，页面不用等菜单回来再挂一次。
  const [menuState, setMenuState] = useState(() => ({
    mode: workMode,
    data: getStorageItem<BackendMenuItem[]>(`${MENU_CACHE_KEY_PREFIX}${workMode}`) ?? [],
  }));
  const menuData = menuState.mode === workMode ? menuState.data : EMPTY_MENUS;
  const setMenuData = useCallback((data: BackendMenuItem[]) => setMenuState({ mode: workMode, data }), [workMode]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    // 先尝试从缓存读取
    const cacheKey = `${MENU_CACHE_KEY_PREFIX}${workMode}`;
    const cached = getStorageItem<BackendMenuItem[]>(cacheKey);
    if (cached && cached.length > 0) {
      setMenuData(cached);
    }

    // 从后端获取最新数据
    const doFetch = async () => {
      try {
        setLoading(true);
        const res = await unifiedApi.get('/menu/user', { params: { work_mode: workMode } });
        if (!cancelled && res?.data && Array.isArray(res.data)) {
          if (res.data.length === 0) {
            console.warn('[Menu] 后端返回空菜单列表，可能是权限配置问题');
          }
          setMenuData(res.data);
          setStorageItem(cacheKey, res.data);
          // 同步写全局菜单缓存，供 AuthContext 刷新恢复使用（登录后不再重复拉菜单）
          setStorageItem(STORAGE_KEYS.MENU_DATA, res.data);
        } else if (!cancelled) {
          // API 返回格式异常 — 清除缓存，避免过期数据持续存在
          console.error('[Menu] 菜单接口返回异常:', res);
          localStorage.removeItem(cacheKey);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[Menu] 获取菜单失败:', error);
          // API 不可用时清除缓存，避免用户长期看到过期菜单
          localStorage.removeItem(cacheKey);
          if (!cached || cached.length === 0) {
            setMenuData([]);
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    doFetch();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workMode, refreshKey]);

  const menuItems = useMemo(() => {
    const dynamicItems = convertMenuItems(menuData, t);
    return ensureHomeMenuItem(dynamicItems, workMode, {
      key: getHomePath(workMode),
      icon: getIconComponent('HomeOutlined'),
      label: t('menu.home'),
    });
  }, [menuData, t, workMode]);

  /** 计算展开的菜单 key（父级菜单的 path） */
  const getOpenKeys = useCallback(
    (pathname: string): string[] => {
      for (const menu of menuData) {
        if (menu.children && menu.children.length > 0) {
          for (const child of menu.children) {
            if (pathname.startsWith(child.path)) {
              return [menu.path];
            }
          }
        }
      }
      return [];
    },
    [menuData],
  );

  /** 刷新菜单缓存（通过清除缓存+更新 state 触发 re-fetch） */
  const refreshMenus = useCallback(() => {
    const cacheKey = `${MENU_CACHE_KEY_PREFIX}${workMode}`;
    localStorage.removeItem(cacheKey);
    setRefreshKey((k) => k + 1);
  }, [workMode]);

  return { menuItems, loading, getOpenKeys, refreshMenus };
};

/**
 * 清除菜单缓存（供菜单管理页面在 CRUD 操作后调用）
 */
export const invalidateMenuCache = () => {
  localStorage.removeItem(`${MENU_CACHE_KEY_PREFIX}business`);
  localStorage.removeItem(`${MENU_CACHE_KEY_PREFIX}admin`);
};
